import { useEffect, useMemo, useState, type FormEvent } from 'react';
import {
  AlertCircle,
  CheckCircle2,
  Info,
  Loader2,
  Pencil,
  Plus,
  RefreshCw,
  RotateCcw,
  Save,
  Trash2,
  X,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuthStore } from '@/lib/auth/store';
import { CubixApiError } from '@/lib/api/types';
import { cn } from '@/lib/utils';

import {
  useCreateRole,
  useDeleteRole,
  usePermissionsMatrix,
  useReplaceRole,
  useResetRole,
  useUpdateRole,
} from './api';
import type {
  PermissionCatalogEntry,
  PermissionGroup,
  PermissionsMatrix,
  RoleEntry,
} from './types';

const GROUP_ORDER: PermissionGroup[] = ['ALM', 'REPORTING', 'DATA', 'ADMIN'];
const GROUP_LABEL: Record<PermissionGroup, string> = {
  ALM:        'ALM',
  REPORTING:  'Reporting',
  DATA:       'Data',
  ADMIN:      'Admin',
};

/**
 * /settings/permissions — Habilitations & Authorizations matrix.
 *
 * Read-only for non-admins; ALM_ADMIN can toggle individual cells and
 * save per-row, or reset a row to platform defaults. Affected users
 * pick up the change at next login (the JWT carries the permission
 * snapshot).
 */
export function PermissionsPage() {
  const isAdmin = useAuthStore((s) => s.hasRole('ALM_ADMIN'));
  const matrix  = usePermissionsMatrix();

  if (matrix.isLoading) {
    return (
      <div className="p-8 text-muted-foreground">
        <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
        Loading permissions matrix…
      </div>
    );
  }
  if (matrix.isError || !matrix.data) {
    return <ApiErrorBanner error={matrix.error ?? new Error('Failed to load matrix')} />;
  }

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Habilitations &amp; Authorizations</h1>
          <p className="text-sm text-muted-foreground">
            Edit which permissions each role grants in this tenant. Changes apply at the
            user's <em>next</em> login — existing sessions keep their old permission set
            until they re-authenticate.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => matrix.refetch()} disabled={matrix.isFetching}>
          {matrix.isFetching ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="mr-2 h-4 w-4" />
          )}
          Reload
        </Button>
      </header>

      {!isAdmin ? (
        <div className="flex items-start gap-2 rounded-md border border-muted bg-muted/30 p-3 text-sm text-muted-foreground">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          <p>You're viewing the matrix in read-only mode. ALM_ADMIN role is required to edit.</p>
        </div>
      ) : null}

      <RoleCatalogCard data={matrix.data} isAdmin={isAdmin} />
      <Matrix data={matrix.data} isAdmin={isAdmin} />
    </div>
  );
}

/**
 * Dynamic Roles (1.28.0) — list of all roles for this tenant with
 * inline add / rename / delete for non-system roles. System roles
 * get a small immutable badge.
 */
function RoleCatalogCard({ data, isAdmin }: { data: PermissionsMatrix; isAdmin: boolean }) {
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');

  const create = useCreateRole({
    onSuccess: () => { setAdding(false); setNewName(''); setNewDesc(''); },
  });

  function handleCreate(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    create.mutate({ roleName: newName, description: newDesc || undefined });
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-3 pb-3">
        <div>
          <CardTitle className="text-base">Roles</CardTitle>
          <CardDescription>
            System roles ship with the platform and cannot be renamed or deleted; their
            permissions are still editable in the matrix below. Custom roles support
            full CRUD; deletion is refused while users still hold the role.
          </CardDescription>
        </div>
        {isAdmin ? (
          <Button size="sm" onClick={() => setAdding((s) => !s)}>
            {adding ? <X className="mr-2 h-4 w-4" /> : <Plus className="mr-2 h-4 w-4" />}
            {adding ? 'Cancel' : 'Add role'}
          </Button>
        ) : null}
      </CardHeader>
      <CardContent className="space-y-3">
        {adding ? (
          <form
            onSubmit={handleCreate}
            className="grid items-end gap-3 md:grid-cols-[10rem_1fr_auto]"
          >
            <div className="space-y-1.5">
              <Label htmlFor="new-role-name">Role name</Label>
              <Input
                id="new-role-name"
                placeholder="COMPLIANCE_OFFICER"
                value={newName}
                onChange={(e) => setNewName(e.target.value.toUpperCase())}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new-role-desc">Description (optional)</Label>
              <Input
                id="new-role-desc"
                placeholder="What does this role do?"
                value={newDesc}
                onChange={(e) => setNewDesc(e.target.value)}
              />
            </div>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Save className="mr-2 h-4 w-4" />
              )}
              Create
            </Button>
            {create.isError ? <ApiErrorBanner error={create.error} /> : null}
          </form>
        ) : null}

        <ul className="divide-y rounded border">
          {data.roles.map((r) => (
            <RoleRow key={r.roleName} role={r} isAdmin={isAdmin} />
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

function RoleRow({ role, isAdmin }: { role: RoleEntry; isAdmin: boolean }) {
  const [editing, setEditing] = useState(false);
  const [name, setName]       = useState(role.roleName);
  const [desc, setDesc]       = useState(role.description ?? '');

  const update = useUpdateRole({ onSuccess: () => setEditing(false) });
  const remove = useDeleteRole();

  const editable = isAdmin && !role.isSystem;

  function handleSave() {
    update.mutate({
      role: role.roleName,
      body: {
        roleName:    name !== role.roleName ? name : undefined,
        description: desc || undefined,
      },
    });
  }

  if (editing) {
    return (
      <li className="flex items-end gap-2 px-3 py-2">
        <div className="space-y-1">
          <Label htmlFor={`r-${role.roleName}-name`} className="text-xs">Name</Label>
          <Input
            id={`r-${role.roleName}-name`}
            value={name}
            onChange={(e) => setName(e.target.value.toUpperCase())}
            className="h-8 w-40"
          />
        </div>
        <div className="flex-1 space-y-1">
          <Label htmlFor={`r-${role.roleName}-desc`} className="text-xs">Description</Label>
          <Input
            id={`r-${role.roleName}-desc`}
            value={desc}
            onChange={(e) => setDesc(e.target.value)}
            className="h-8"
          />
        </div>
        <Button size="sm" onClick={handleSave} disabled={update.isPending}>
          {update.isPending ? (
            <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
          ) : (
            <Save className="mr-1 h-3.5 w-3.5" />
          )}
          Save
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
          Cancel
        </Button>
        {update.isError ? (
          <span className="text-xs text-destructive">{update.error.message}</span>
        ) : null}
      </li>
    );
  }

  return (
    <li className="flex items-center justify-between gap-3 px-3 py-2">
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <code className="font-mono text-sm font-medium">{role.roleName}</code>
          {role.isSystem ? (
            <span className="rounded bg-secondary px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-secondary-foreground">
              system
            </span>
          ) : (
            <span className="rounded bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-700">
              custom
            </span>
          )}
        </div>
        {role.description ? (
          <p className="mt-0.5 text-xs text-muted-foreground">{role.description}</p>
        ) : null}
      </div>
      {editable ? (
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={() => setEditing(true)} title="Rename / edit description">
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            disabled={remove.isPending}
            onClick={() => {
              if (confirm(`Delete role ${role.roleName}? Permission grants will be removed too.`)) {
                remove.mutate(role.roleName);
              }
            }}
            title="Delete"
          >
            <Trash2 className="h-3.5 w-3.5 text-destructive" />
          </Button>
        </div>
      ) : null}
      {remove.isError ? (
        <span className="text-xs text-destructive" title={remove.error.message}>
          Cannot delete
        </span>
      ) : null}
    </li>
  );
}

function Matrix({ data, isAdmin }: { data: PermissionsMatrix; isAdmin: boolean }) {
  // Group catalog entries for rendering.
  const grouped = useMemo(() => {
    const out: Record<PermissionGroup, PermissionCatalogEntry[]> = {
      ALM: [], REPORTING: [], DATA: [], ADMIN: [],
    };
    for (const entry of data.catalog) out[entry.group].push(entry);
    return out;
  }, [data.catalog]);

  return (
    <div className="space-y-4">
      {data.roles.map((role) => (
        <RoleCard
          key={role.roleName}
          role={role}
          grouped={grouped}
          initialGrants={new Set(data.matrix[role.roleName] ?? [])}
          isAdmin={isAdmin}
        />
      ))}
    </div>
  );
}

/**
 * One card per role, with the catalog presented as grouped checkbox columns.
 * Local state holds the WIP edit; Save sends a bulk PUT for that role.
 */
function RoleCard({
  role,
  grouped,
  initialGrants,
  isAdmin,
}: {
  role: RoleEntry;
  grouped: Record<PermissionGroup, PermissionCatalogEntry[]>;
  initialGrants: Set<string>;
  isAdmin: boolean;
}) {
  const [grants, setGrants] = useState<Set<string>>(initialGrants);
  const [dirty, setDirty]   = useState(false);

  // Re-hydrate when the parent matrix reloads.
  useEffect(() => {
    setGrants(new Set(initialGrants));
    setDirty(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Array.from(initialGrants).sort().join(',')]);

  const replace = useReplaceRole({
    onSuccess: (saved) => { setGrants(new Set(saved)); setDirty(false); },
  });
  const reset = useResetRole({
    onSuccess: (saved) => { setGrants(new Set(saved)); setDirty(false); },
  });

  function toggle(key: string, on: boolean) {
    if (!isAdmin) return;
    const next = new Set(grants);
    if (on) next.add(key); else next.delete(key);
    setGrants(next);
    setDirty(true);
  }

  return (
    <Card className={cn(dirty ? 'border-amber-500/40' : '')}>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          {role.roleName}
          {role.isSystem ? (
            <span className="rounded bg-secondary px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-secondary-foreground">
              system
            </span>
          ) : (
            <span className="rounded bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-700">
              custom
            </span>
          )}
          <span className="text-xs font-normal text-muted-foreground">
            ({grants.size} permission{grants.size === 1 ? '' : 's'} granted)
          </span>
          {dirty ? (
            <span className="rounded bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-medium uppercase text-amber-700">
              Unsaved
            </span>
          ) : null}
        </CardTitle>
        <CardDescription>
          {role.description ? <>{role.description}<br /></> : null}
          Toggle individual permissions, then Save.{' '}
          {role.isSystem
            ? <>Reset returns this role to the platform default mapping.</>
            : <>Custom roles have no built-in defaults; manage grants explicitly.</>}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {GROUP_ORDER.map((group) => {
            const entries = grouped[group];
            if (!entries.length) return null;
            return (
              <div key={group}>
                <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {GROUP_LABEL[group]}
                </h4>
                <ul className="space-y-1">
                  {entries.map((p) => {
                    const checked = grants.has(p.key);
                    return (
                      <li key={p.key} className="flex items-start gap-2">
                        <input
                          type="checkbox"
                          id={`${role.roleName}-${p.key}`}
                          checked={checked}
                          disabled={!isAdmin}
                          onChange={(e) => toggle(p.key, e.target.checked)}
                          className="mt-1 h-4 w-4 cursor-pointer disabled:cursor-not-allowed"
                        />
                        <label
                          htmlFor={`${role.roleName}-${p.key}`}
                          className="cursor-pointer text-xs"
                          title={p.description}
                        >
                          <code className="font-mono">{p.key}</code>
                          <span className="mt-0.5 block text-[10px] text-muted-foreground">
                            {p.description}
                          </span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>

        {(replace.isError || reset.isError) ? (
          <div className="mt-3">
            {replace.isError ? <ApiErrorBanner error={replace.error} /> : null}
            {reset.isError   ? <ApiErrorBanner error={reset.error}   /> : null}
          </div>
        ) : null}
        {(replace.isSuccess || reset.isSuccess) && !dirty ? (
          <p className="mt-3 flex items-center gap-1.5 text-sm text-emerald-600">
            <CheckCircle2 className="h-4 w-4" />
            {reset.isSuccess ? 'Reset to platform defaults.' : 'Saved.'}
          </p>
        ) : null}

        <div className="mt-3 flex items-center justify-end gap-2">
          {role.isSystem ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={!isAdmin || reset.isPending}
              onClick={() => reset.mutate(role.roleName)}
            >
              {reset.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <RotateCcw className="mr-2 h-4 w-4" />
              )}
              Reset to defaults
            </Button>
          ) : null}
          <Button
            type="button"
            size="sm"
            disabled={!isAdmin || !dirty || replace.isPending}
            onClick={() =>
              replace.mutate({ role: role.roleName, permissionKeys: Array.from(grants).sort() })
            }
          >
            {replace.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            Save
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function ApiErrorBanner({ error }: { error: CubixApiError | Error }) {
  const isCubix = error instanceof CubixApiError;
  return (
    <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
      <div className="flex-1">
        <p className="font-medium">{error.message}</p>
        {isCubix ? (
          <p className="mt-0.5 text-xs opacity-80">code <code>{error.code}</code></p>
        ) : null}
      </div>
    </div>
  );
}
